import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, Save, Loader2, ExternalLink, Phone, MapPin,
  Image as ImageIcon, Check, Globe,
} from "lucide-react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);
const TABS = ["Content", "Products", "Detailers", "Enquiries"];

export default function LandingAdmin() {
  const [tab, setTab] = useState("Content");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: d } = await api.get("/landing/admin");
      setData(d);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const newCount = (data?.enquiries || []).filter((e) => e.status === "new").length;

  return (
    <div data-testid="landing-admin">
      <PageHead title="Landing Page" subtitle="Manage your public website at veloskinppf.com">
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-3 py-2 text-sm font-medium text-brand hover:bg-brand-tint" data-testid="view-site-btn">
          <Globe className="h-4 w-4" /> View site <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </PageHead>

      <div className="mb-6 inline-flex flex-wrap gap-1 rounded-lg border border-brand-line bg-white p-1">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`landing-tab-${t.toLowerCase()}`}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === t ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-tint"}`}>
            {t}{t === "Enquiries" && newCount > 0 && (
              <span className="ml-1.5 rounded-full bg-crimson-500 px-1.5 py-0.5 text-[10px] text-white">{newCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {tab === "Content" && <ContentTab data={data} reload={load} />}
          {tab === "Products" && <ProductsTab products={data?.products || []} reload={load} />}
          {tab === "Detailers" && <DetailersTab detailers={data?.detailers || []} reload={load} />}
          {tab === "Enquiries" && <EnquiriesTab enquiries={data?.enquiries || []} reload={load} />}
        </>
      )}
    </div>
  );
}

/* ---------------- CONTENT ---------------- */
function ContentTab({ data, reload }) {
  const c = data?.content || {};
  const [hero, setHero] = useState({ ...c.hero, phones: (c.hero?.phones || []).join(", ") });
  const [academy, setAcademy] = useState({ ...c.academy });
  const [seo, setSeo] = useState({ ...c.seo });
  const [busy, setBusy] = useState(false);
  const heroImgRef = useRef(null);
  const heroLogoFromDb = c.hero?.logo;
  const academyImgRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/landing/content", {
        hero: { brand: hero.brand, headline: hero.headline, subheadline: hero.subheadline,
          cta_label: hero.cta_label, phones: (hero.phones || "").split(",").map((p) => p.trim()).filter(Boolean) },
        academy: { title: academy.title, subtitle: academy.subtitle, description: academy.description, closing: academy.closing },
        seo: { title: seo.title, description: seo.description },
      });
      toast.success("Content saved");
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadImg = async (endpoint, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(endpoint, fd);
      toast.success("Image uploaded");
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const field = "mt-1 w-full";
  return (
    <div className="space-y-6">
      <Card className="border-brand-line shadow-none">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ink">Hero section</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <L label="Headline (H1)"><Input className={field} value={hero.headline || ""} onChange={(e) => setHero({ ...hero, headline: e.target.value })} data-testid="hero-headline-input" /></L>
            <L label="CTA button label"><Input className={field} value={hero.cta_label || ""} onChange={(e) => setHero({ ...hero, cta_label: e.target.value })} /></L>
          </div>
          <L label="Subheadline"><Textarea className={field} rows={2} value={hero.subheadline || ""} onChange={(e) => setHero({ ...hero, subheadline: e.target.value })} /></L>
          <L label="Phone numbers (comma-separated)"><Input className={field} value={hero.phones || ""} onChange={(e) => setHero({ ...hero, phones: e.target.value })} /></L>
          <div className="grid gap-4 md:grid-cols-2">
            <ImageField label="Hero image" current={c.hero?.image} inputRef={heroImgRef}
              onFile={(f) => uploadImg("/landing/content/hero-image", f)} testid="hero-image" />
            <div className="flex items-center gap-3 rounded-lg border border-brand-line p-3">
              {heroLogoFromDb && <img src={mediaUrl(heroLogoFromDb)} alt="logo" className="h-10 w-auto" />}
              <span className="text-xs text-slate-500">Brand logo (set at setup)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ink">Detailing Academy</h2>
          <L label="Title"><Input className={field} value={academy.title || ""} onChange={(e) => setAcademy({ ...academy, title: e.target.value })} /></L>
          <L label="Subtitle"><Input className={field} value={academy.subtitle || ""} onChange={(e) => setAcademy({ ...academy, subtitle: e.target.value })} /></L>
          <L label="Description"><Textarea className={field} rows={2} value={academy.description || ""} onChange={(e) => setAcademy({ ...academy, description: e.target.value })} /></L>
          <L label="Closing line"><Textarea className={field} rows={2} value={academy.closing || ""} onChange={(e) => setAcademy({ ...academy, closing: e.target.value })} /></L>
          <p className="text-xs text-slate-400">The "Why train with us" points and course details are managed in code — ask to update them anytime.</p>
          <ImageField label="Academy flyer" current={c.academy?.image} inputRef={academyImgRef}
            onFile={(f) => uploadImg("/landing/content/academy-image", f)} testid="academy-image" />
        </CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-semibold text-brand-ink">SEO</h2>
          <L label="Page title"><Input className={field} value={seo.title || ""} onChange={(e) => setSeo({ ...seo, title: e.target.value })} data-testid="seo-title-input" /></L>
          <L label="Meta description"><Textarea className={field} rows={2} value={seo.description || ""} onChange={(e) => setSeo({ ...seo, description: e.target.value })} /></L>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-deep" data-testid="save-content-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save all content
      </Button>
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {children}
  </label>
);

function ImageField({ label, current, inputRef, onFile, testid }) {
  return (
    <div className="rounded-lg border border-brand-line p-3">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        {current ? (
          <img src={mediaUrl(current)} alt={label} className="h-16 w-16 rounded object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded bg-brand-tint text-brand"><ImageIcon className="h-6 w-6" /></div>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload-btn`}>
          <Upload className="mr-2 h-3.5 w-3.5" /> Upload
        </Button>
      </div>
    </div>
  );
}

/* ---------------- PRODUCTS ---------------- */
const emptyProduct = { name: "", brand: "", tagline: "", description: "", features: "", active: true };

function ProductsTab({ products, reload }) {
  const [editing, setEditing] = useState(null); // product id or "new"
  const [form, setForm] = useState(emptyProduct);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, features: (p.features || []).join("\n") });
  };
  const startNew = () => { setEditing("new"); setForm(emptyProduct); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setBusy(true);
    const payload = { name: form.name, brand: form.brand, tagline: form.tagline,
      description: form.description, active: form.active,
      features: (form.features || "").split("\n").map((f) => f.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/landing/products", payload);
      else await api.put(`/landing/products/${editing}`, payload);
      toast.success("Product saved");
      setEditing(null); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this product from the website?")) return;
    try { await api.delete(`/landing/products/${id}`); toast.success("Product removed"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uploadImg = async (file) => {
    if (!file || editing === "new") { toast.error("Save the product first, then upload an image"); return; }
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/landing/products/${editing}/image`, fd); toast.success("Image uploaded"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Button onClick={startNew} className="bg-brand hover:bg-brand-deep" data-testid="add-product-btn">
        <Plus className="mr-2 h-4 w-4" /> Add product / service
      </Button>

      {editing && (
        <Card className="border-crimson-200 shadow-none" data-testid="product-editor">
          <CardContent className="space-y-4 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <L label="Name"><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></L>
              <L label="Brand"><Input className="mt-1" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></L>
            </div>
            <L label="Tagline"><Input className="mt-1" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></L>
            <L label="Description"><Textarea className="mt-1" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></L>
            <L label="Features (one per line)"><Textarea className="mt-1" rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="product-features-input" /></L>
            {editing !== "new" && (
              <div className="flex items-center gap-3">
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImg(e.target.files?.[0]); e.target.value = ""; }} data-testid="product-image-input" />
                <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()}><Upload className="mr-2 h-3.5 w-3.5" /> Upload flyer image</Button>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-deep" data-testid="save-product-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <Card key={p.id} className="border-brand-line shadow-none" data-testid={`admin-product-${p.id}`}>
            <CardContent className="p-4">
              <div className="flex gap-3">
                {p.image ? <img src={mediaUrl(p.image)} alt={p.name} className="h-20 w-16 shrink-0 rounded object-cover" />
                  : <div className="flex h-20 w-16 shrink-0 items-center justify-center rounded bg-brand-tint text-brand"><ImageIcon className="h-5 w-5" /></div>}
                <div className="min-w-0">
                  <p className="truncate font-semibold text-brand-ink">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.brand}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.tagline}</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`edit-product-${p.id}`}>Edit</Button>
                <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- DETAILERS ---------------- */
const emptyDetailer = { name: "", city: "", phone: "", maps_url: "", active: true };

function DetailersTab({ detailers, reload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDetailer);
  const [busy, setBusy] = useState(false);

  const startEdit = (d) => { setEditing(d.id); setForm({ ...d }); };
  const startNew = () => { setEditing("new"); setForm(emptyDetailer); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing === "new") await api.post("/landing/detailers", form);
      else await api.put(`/landing/detailers/${editing}`, form);
      toast.success("Detailer saved"); setEditing(null); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this detailer from the website?")) return;
    try { await api.delete(`/landing/detailers/${id}`); toast.success("Detailer removed"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Button onClick={startNew} className="bg-brand hover:bg-brand-deep" data-testid="add-detailer-btn">
        <Plus className="mr-2 h-4 w-4" /> Add detailer
      </Button>

      {editing && (
        <Card className="border-crimson-200 shadow-none" data-testid="detailer-editor">
          <CardContent className="space-y-4 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <L label="Name"><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="detailer-name-input" /></L>
              <L label="City"><Input className="mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></L>
              <L label="Phone"><Input className="mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></L>
              <L label="Google Maps link"><Input className="mt-1" value={form.maps_url} onChange={(e) => setForm({ ...form, maps_url: e.target.value })} placeholder="https://maps.google.com/..." data-testid="detailer-maps-input" /></L>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-deep" data-testid="save-detailer-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {detailers.length === 0 ? (
        <EmptyState icon={MapPin} title="No detailers yet" hint="Add accredited detailers to show them on the website." testid="no-detailers" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-brand-line bg-white">
          <table className="w-full text-sm">
            <thead className="bg-brand-tint text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">City</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Maps</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {detailers.map((d) => (
                <tr key={d.id} className="border-t border-brand-line" data-testid={`admin-detailer-${d.id}`}>
                  <td className="px-4 py-3 font-medium text-brand-ink">{d.name}</td>
                  <td className="px-4 py-3 text-slate-600">{d.city || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{d.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{d.phone}</span> : "—"}</td>
                  <td className="px-4 py-3">{d.maps_url ? <a href={d.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline"><MapPin className="h-3.5 w-3.5" /> Link</a> : <span className="text-rose-500">Missing</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" onClick={() => startEdit(d)} data-testid={`edit-detailer-${d.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => del(d.id)} data-testid={`delete-detailer-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- ENQUIRIES ---------------- */
function EnquiriesTab({ enquiries, reload }) {
  const mark = async (id, status) => {
    try { await api.patch(`/landing/enquiries/${id}`, { status }); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (enquiries.length === 0)
    return <EmptyState icon={Globe} title="No enquiries yet" hint="Website enquiries will appear here." testid="no-enquiries" />;
  return (
    <div className="overflow-hidden rounded-lg border border-brand-line bg-white">
      <table className="w-full text-sm">
        <thead className="bg-brand-tint text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Message</th><th className="px-4 py-3"></th></tr>
        </thead>
        <tbody>
          {enquiries.map((e) => (
            <tr key={e.id} className={`border-t border-brand-line ${e.status === "new" ? "bg-crimson-50/40" : ""}`} data-testid={`enquiry-row-${e.id}`}>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{fmtDateTime(e.created_at)}</td>
              <td className="px-4 py-3"><Badge variant="outline">{e.type}</Badge></td>
              <td className="px-4 py-3 font-medium text-brand-ink">{e.name}</td>
              <td className="px-4 py-3"><a href={`tel:${e.phone}`} className="text-brand hover:underline">{e.phone}</a></td>
              <td className="px-4 py-3 text-slate-600">{e.location || "—"}</td>
              <td className="max-w-xs px-4 py-3 text-slate-600">{e.message || "—"}</td>
              <td className="px-4 py-3 text-right">
                {e.status === "new" ? (
                  <Button variant="outline" size="sm" onClick={() => mark(e.id, "handled")} data-testid={`mark-handled-${e.id}`}>
                    <Check className="mr-1 h-3.5 w-3.5" /> Mark handled
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" /> Handled</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
